import { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { API, LOGO_URL } from "@/config";
import {
  DEFAULT_INCOMES, DEFAULT_EXPENSES, DEFAULT_SAVINGS,
  DEFAULT_BUDGET_BUCKETS, DEFAULT_INVESTMENT_ASSETS, DEFAULT_LIABILITIES
} from "@/data/defaults";
import apiClient from "@/utils/apiClient";

import LoginPage from "@/components/auth/LoginPage";
import RegisterPage from "@/components/auth/RegisterPage";
import OtpVerifyPage from "@/components/auth/OtpVerifyPage";
import CashFlowDashboard from "@/components/dashboard/CashFlowDashboard";
import BudgetPlanner from "@/components/budget/BudgetPlanner";
import AnalyticsPage from "@/components/analytics/AnalyticsPage";
import NetWorthCalculator from "@/components/networth/NetWorthCalculator";
import PortfolioCalculator from "@/components/portfolio/PortfolioCalculator";
import FinancialCalculators from "@/components/calculators/FinancialCalculators";

const SALARY_SUBCATEGORIES = [
  "Salary Income – Self",
  "Salary Income – Spouse",
  "Bonus/Incentive – Self",
  "Bonus/Incentive – Spouse",
];

function migrateIncomes(incomes) {
  const hasSalarySubcategories = incomes.some(i => i.category === "Salary Income" && i.subcategory);
  if (hasSalarySubcategories) return incomes;

  const oldSalary = incomes.find(i => i.category === "Salary Income");
  const others = incomes.filter(i => i.category !== "Salary Income");

  const salaryItems = SALARY_SUBCATEGORIES.map((sub, idx) => ({
    category: "Salary Income",
    subcategory: sub,
    monthly_amount: idx === 0 && oldSalary ? oldSalary.monthly_amount : 0,
    yearly_amount: idx === 0 && oldSalary ? oldSalary.yearly_amount : 0,
    is_monthly: idx === 0 && oldSalary ? oldSalary.is_monthly : true,
  }));

  return [...salaryItems, ...others];
}

const INITIAL_DATA = {
  id: null,
  incomes: DEFAULT_INCOMES,
  expenses: DEFAULT_EXPENSES,
  savings: DEFAULT_SAVINGS,
  monthly_salary: 0,
  budget_buckets: DEFAULT_BUDGET_BUCKETS,
  investment_assets: DEFAULT_INVESTMENT_ASSETS,
  liabilities: DEFAULT_LIABILITIES,
};

function migrateInvestmentAssets(assets) {
  if (!assets) return DEFAULT_INVESTMENT_ASSETS;
  const migrated = { ...assets };
  if (!migrated.real_estate_assets || migrated.real_estate_assets.length === 0) {
    migrated.real_estate_assets = DEFAULT_INVESTMENT_ASSETS.real_estate_assets;
  }
  const hasNewEquity = migrated.equity_assets.some(a => a.name === "PMS/AIF/SIF");
  if (!hasNewEquity) {
    migrated.equity_assets = [
      ...migrated.equity_assets,
      ...DEFAULT_INVESTMENT_ASSETS.equity_assets.filter(d => !migrated.equity_assets.some(e => e.name === d.name))
    ];
  }
  return migrated;
}

function migrateExpenses(expenses) {
  const hasCharity = expenses.some(e => e.subcategory === "Charity/Donation");
  if (hasCharity) return expenses;
  return [
    ...expenses,
    { category: "Miscellaneous", subcategory: "Charity/Donation", monthly_amount: 0, yearly_amount: 0, is_monthly: true },
    { category: "Miscellaneous", subcategory: "Learning/Self-Development", monthly_amount: 0, yearly_amount: 0, is_monthly: true },
  ];
}

export default function AppRoutes({ user, setUser: setUserExternal, login, register, logout }) {
  const [data, setData] = useState(INITIAL_DATA);
  const [loading, setLoading] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isAuthenticated = !!user;

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`${API}/financial-data/my`);
        if (cancelled) return;
        setData({
          id: response.data.id,
          incomes: migrateIncomes(response.data.incomes || DEFAULT_INCOMES),
          expenses: migrateExpenses(response.data.expenses || DEFAULT_EXPENSES),
          savings: response.data.savings || DEFAULT_SAVINGS,
          monthly_salary: response.data.monthly_salary || 0,
          budget_buckets: response.data.budget_buckets || DEFAULT_BUDGET_BUCKETS,
          investment_assets: migrateInvestmentAssets(response.data.investment_assets || DEFAULT_INVESTMENT_ASSETS),
          liabilities: response.data.liabilities || DEFAULT_LIABILITIES,
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error("Error loading data:", error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const saveToServer = useCallback(async (dataToSave) => {
    if (!isAuthenticated) return;
    try {
      await apiClient.put(`${API}/financial-data/my`, {
        incomes: dataToSave.incomes,
        expenses: dataToSave.expenses,
        savings: dataToSave.savings,
        monthly_salary: dataToSave.monthly_salary,
        budget_buckets: dataToSave.budget_buckets,
        investment_assets: dataToSave.investment_assets,
        liabilities: dataToSave.liabilities,
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Error saving data:", error);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || data.incomes.length === 0) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveToServer(data), 1000);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [data, isAuthenticated, saveToServer]);

  const handleUpdateIncomes = useCallback((index, updatedIncome) => {
    setData(prev => { const newIncomes = [...prev.incomes]; newIncomes[index] = updatedIncome; return { ...prev, incomes: newIncomes }; });
  }, []);

  const handleUpdateExpenses = useCallback((index, updatedExpense) => {
    setData(prev => { const newExpenses = [...prev.expenses]; newExpenses[index] = updatedExpense; return { ...prev, expenses: newExpenses }; });
  }, []);

  const handleUpdateSavings = useCallback((index, updatedSaving) => {
    setData(prev => { const newSavings = [...prev.savings]; newSavings[index] = updatedSaving; return { ...prev, savings: newSavings }; });
  }, []);

  const handleUpdateSalary = useCallback((salary) => {
    setData(prev => ({ ...prev, monthly_salary: salary }));
  }, []);

  const handleUpdateBuckets = useCallback((buckets) => {
    setData(prev => ({ ...prev, budget_buckets: buckets }));
  }, []);

  const handleUpdateInvestmentAssets = useCallback((category, index, amount) => {
    setData(prev => {
      const newAssets = { ...prev.investment_assets };
      newAssets[category] = [...newAssets[category]];
      newAssets[category][index] = { ...newAssets[category][index], amount };
      return { ...prev, investment_assets: newAssets };
    });
  }, []);

  const handleUpdateLiabilities = useCallback((index, amount) => {
    setData(prev => {
      const newLiabilities = [...prev.liabilities];
      newLiabilities[index] = { ...newLiabilities[index], amount };
      return { ...prev, liabilities: newLiabilities };
    });
  }, []);

  if (loading) {
    return (
      <div className="finance-app min-h-screen flex items-center justify-center" data-testid="loading-data">
        <div className="text-center">
          <img src={LOGO_URL} alt="SubiWealth" className="h-16 mx-auto mb-4" />
          <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-teal-600 font-medium">Loading your finances...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" /> : <RegisterPage />} />
      <Route path="/verify-otp" element={user ? <Navigate to="/" /> : <OtpVerifyPage onAuthSuccess={setUserExternal} />} />
      <Route path="/forgot-password" element={<Navigate to="/login" />} />
      <Route path="/reset-password" element={<Navigate to="/login" />} />
      <Route path="/" element={user ? <CashFlowDashboard data={data} onUpdateIncomes={handleUpdateIncomes} onUpdateExpenses={handleUpdateExpenses} onUpdateSavings={handleUpdateSavings} user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="/budget-planner" element={user ? <BudgetPlanner monthlySalary={data.monthly_salary} budgetBuckets={data.budget_buckets} onUpdateSalary={handleUpdateSalary} onUpdateBuckets={handleUpdateBuckets} user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="/analytics" element={user ? <AnalyticsPage data={data} user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="/net-worth" element={user ? <NetWorthCalculator investmentAssets={data.investment_assets} liabilities={data.liabilities} onUpdateInvestmentAssets={handleUpdateInvestmentAssets} onUpdateLiabilities={handleUpdateLiabilities} user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="/portfolio" element={user ? <PortfolioCalculator investmentAssets={data.investment_assets} user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="/calculators" element={user ? <FinancialCalculators user={user} onLogout={logout} /> : <Navigate to="/login" />} />
    </Routes>
  );
}
